import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isFullSongVideoTheme } from 'fym-shared/src/music24/oneOffPublish';
import type { StoreType } from '../../../../redux/store';
import { usePromoteChannels } from '../hooks/usePromoteChannels';
import { initialStudioPublishChoice, type StudioPublishChoice } from '../publish/studioPublishChoice';
import type { AddAssetRequest } from './addAssetDialogStore';
import {
  addedUploadKey,
  clearAddedUpload,
  setAddedUploadPublishChoice,
  startAddedUpload,
  stopAddedUpload,
  useAddedUpload,
} from './addedUploadStore';

/**
 * The state of the upload tab (design a4): the upload of this song and asset type (it goes on when
 * the dialog closes), the pick of a file, stop, and the "Also publish it on YouTube" switch. The
 * switch shows only for a long video and a connected YouTube channel.
 */
export function useAddedUploadTab(request: AddAssetRequest) {
  const { accountId, catalogItemUuid, themeId } = request;
  const dispatch = useDispatch<StoreType['dispatch']>();
  const upload = useAddedUpload(catalogItemUuid, themeId);
  const key = addedUploadKey(catalogItemUuid, themeId);

  const { channels } = usePromoteChannels({ accountId });
  const youtubeConnected = useMemo(
    () =>
      channels.some(
        (channel) => channel.platform === 'youtube' && channel.status === 'connected' && channel.connection !== null
      ),
    [channels]
  );
  const offersPublish = isFullSongVideoTheme(themeId) && youtubeConnected;

  const [localChoice, setLocalChoice] = useState<StudioPublishChoice>(() =>
    initialStudioPublishChoice(request.releaseDate)
  );
  const choice = upload?.publishChoice ?? localChoice;
  const changeChoice = useCallback(
    (next: StudioPublishChoice) => {
      setLocalChoice(next);
      setAddedUploadPublishChoice(key, next);
    },
    [key]
  );

  const pickFile = useCallback(
    (file: File) => {
      startAddedUpload(
        { dispatch },
        {
          accountId,
          catalogItemUuid,
          themeId,
          file,
          songDurationSec: request.songDurationSec,
          releaseDate: request.releaseDate,
          publishChoice: offersPublish ? choice : null,
        }
      ).catch(console.error);
    },
    [accountId, catalogItemUuid, choice, dispatch, offersPublish, request.releaseDate, request.songDurationSec, themeId]
  );

  const stop = useCallback(() => stopAddedUpload(key), [key]);
  const reset = useCallback(() => clearAddedUpload(key), [key]);

  return { upload, pickFile, stop, reset, offersPublish, choice, changeChoice };
}
